import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FeedInputContext, InputOptionResource } from '@app/@feed-inputs/index';
import { SquareBracketsHttpUrlEncodingCodec } from '@core/http/square-brackets-http-url-encoding-codec';
import { DataResource } from '@shared/interfaces/data-resource';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class FeedInputService {
  ENDPOINT = '/feed-input';
  cache = new Map<string, Observable<DataResource<InputOptionResource[]>>>();

  constructor(private httpClient: HttpClient) {}

  getList(entity: string, context?: FeedInputContext | number): Observable<DataResource<InputOptionResource[]>> {
    const cacheKey = this.getCacheKey(entity, context);

    if (!this.cache.has(cacheKey)) {
      let params = new HttpParams({
        encoder: new SquareBracketsHttpUrlEncodingCodec(),
      }).append('paginate', '0');

      if (context) {
        if (typeof context === 'object') {
          params = Object.keys(context).reduce(
            (acc, key) => acc.append(FeedInputService.getParamNameByKey(key), (context as any)[key]),
            params,
          );
        } else {
          params = params.append('context', context.toString());
        }
      }

      const request$ = this.httpClient
        .get<DataResource<InputOptionResource[]>>(`${this.ENDPOINT}/${entity}`, {
          params,
        })
        .pipe(shareReplay(1));

      this.cache.set(cacheKey, request$);
    }

    return this.cache.get(cacheKey)!;
  }

  clearCache(): void {
    this.cache.clear();
  }

  refreshCacheForEntity(entity: string): Observable<DataResource<InputOptionResource[]>> {
    for (const key of this.cache.keys()) {
      if (key === entity || key.startsWith(`${entity}?`)) {
        this.cache.delete(key);
      }
    }
    return this.getList(entity);
  }

  private getCacheKey(entity: string, context?: FeedInputContext | number): string {
    if (!context) {
      return entity;
    }
    if (typeof context === 'object') {
      return `${entity}?${JSON.stringify(
        Object.keys(context)
          .sort()
          .reduce((acc, key) => ({ ...acc, [key]: (context as any)[key] }), {}),
      )}`;
    }
    return `${entity}?context=${context}`;
  }

  private static getParamNameByKey(key: string): string {
    return `context[${key}]`;
  }
}
